import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import styles from './Sidebar.module.css';
import logo from '../assets/logo.png';

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <path d={d} />
  </svg>
);

const ICONS = {
  home:    'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z',
  book:    'M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z',
  msg:     'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
  bell:    'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0',
  file:    'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6',
  task:    'M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11',
  star:    'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  shield:  'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  support: 'M3 18v-6a9 9 0 0118 0v6M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z',
  logout:  'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9',
};

export default function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [asignaturas, setAsignaturas] = useState([]);
  const [expanded, setExpanded]       = useState(null);
  const [perfil, setPerfil]           = useState(null);
  const isAdmin = user?.rol === 'admin';

  useEffect(() => {
    api.get('/usuarios/me').then(r => setPerfil(r.data.data)).catch(() => {});
    if (!isAdmin) {
      api.get('/asignaturas').then(r => setAsignaturas(r.data.data)).catch(() => {});
    }
  }, [isAdmin]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const rolLabel = r => ({ admin: '🛡️ Admin', profesor: 'Profesor', alumno: 'Alumno' }[r] || r);

  const isMobile = () => window.innerWidth <= 768;

  const navegar = (to) => {
    navigate(to);
    if (onClose && isMobile()) onClose(); // solo cerrar en móvil
  };

  return (
    <aside className={`${styles.sidebar} ${open ? styles.open : styles.closed}`}>
      {/* Logo */}
      <div className={styles.logo}>
        <img src={logo} alt="EduPortal" className={styles.logoImg} />
        {open && <span className={styles.logoText}>EduPortal</span>}
      </div>

      {/* Tarjeta de usuario */}
      <button className={styles.userCardBtn} onClick={() => navegar('/perfil')} title="Ver mi perfil">
        <div className={styles.avatarWrap}>
          {perfil?.avatar_url ? (
            <img src={perfil.avatar_url} alt="avatar" className={styles.avatarImg} />
          ) : (
            <div className={styles.avatar}>{user?.nombre?.[0]}{user?.apellidos?.[0]}</div>
          )}
        </div>
        {open && (
          <div className={styles.userInfo}>
            <p className={styles.userName}>{user?.nombre} {user?.apellidos}</p>
            <p className={styles.userRole}>{rolLabel(user?.rol)}</p>
            {perfil?.carrera_nombre && (
              <p className={styles.userCarrera}>🎓 {perfil.carrera_nombre}</p>
            )}
            {!perfil?.carrera_nombre && user?.rol === 'alumno' && (
              <p className={styles.userCarreraNone}>Sin carrera asignada</p>
            )}
            <p className={styles.editHint}>Editar perfil →</p>
          </div>
        )}
      </button>

      <nav className={styles.nav}>
        {/* Inicio */}
        <NavLink to="/dashboard"
          className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
          onClick={() => isMobile() && onClose && onClose()}>
          <Icon d={ICONS.home} />
          {open && <span>Inicio</span>}
        </NavLink>

        {/* Admin */}
        {isAdmin && (
          <>
            {open && <p className={styles.section}>Administración</p>}
            <NavLink to="/admin"
              className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
              onClick={() => isMobile() && onClose && onClose()}>
              <Icon d={ICONS.shield} />
              {open && <span>Panel de Admin</span>}
            </NavLink>
          </>
        )}

        {/* Asignaturas — no admin */}
        {!isAdmin && (
          <>
            {open && <p className={styles.section}>Mis asignaturas</p>}
            {asignaturas.map(a => (
              <div key={a.id}>
                <button
                  className={`${styles.navItem} ${expanded === a.id ? styles.active : ''}`}
                  onClick={() => {
                    setExpanded(expanded === a.id ? null : a.id);
                    navegar(`/asignaturas/${a.id}`);
                  }}>
                  <Icon d={ICONS.book} />
                  {open && (
                    <>
                      <span className={styles.asigName}>{a.nombre}</span>
                      <span className={styles.chevron}>{expanded === a.id ? '▴' : '▾'}</span>
                    </>
                  )}
                </button>
                {open && expanded === a.id && (
                  <div className={styles.submenu}>
                    {[
                      { to: 'anuncios',       icon: ICONS.bell,  label: 'Anuncios' },
                      { to: 'mensajes',       icon: ICONS.msg,   label: 'Mensajes' },
                      { to: 'contenidos',     icon: ICONS.file,  label: 'Contenidos' },
                      { to: 'trabajos',       icon: ICONS.task,  label: 'Trabajos' },
                      { to: 'calificaciones', icon: ICONS.star,  label: 'Calificaciones' },
                    ].map(item => (
                      <NavLink key={item.to}
                        to={`/asignaturas/${a.id}/${item.to}`}
                        className={({ isActive }) => `${styles.subItem} ${isActive ? styles.subActive : ''}`}
                        onClick={() => isMobile() && onClose && onClose()}>
                        <Icon d={item.icon} />
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* Soporte técnico — solo para no-admins */}
        {!isAdmin && (
          <>
            {open && <p className={styles.section}>Ayuda</p>}
            <NavLink to="/soporte"
              className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
              onClick={() => isMobile() && onClose && onClose()}>
              <Icon d={ICONS.support} />
              {open && <span>Soporte técnico</span>}
            </NavLink>
          </>
        )}
      </nav>

      <button className={styles.logout} onClick={handleLogout}>
        <Icon d={ICONS.logout} />
        {open && <span>Cerrar sesión</span>}
      </button>
    </aside>
  );
}
